<script lang="ts">
  /**
   * A little aeroplane flying an endless loop: `---->O----->`
   *
   * It is the brand mark in the header and the loading indicator everywhere else, deliberately
   * the same object in both places — a logo that animates while you wait is one idea, not two.
   *
   * How it moves: a CSS motion path (`offset-path`) with `offset-rotate: auto`, so the plane
   * banks into the loop by itself instead of needing keyframed rotation.
   *
   * **The plane is an SVG element, not an HTML one, and that is load-bearing.** `offset-path`
   * resolves its coordinates in CSS pixels for an HTML element — so a `<span>` following this
   * path inside a 96 px logo would fly a 124 px loop straight out of the box. Inside the SVG
   * with `transform-box: view-box`, the same path is read in viewBox units and scales with the
   * drawing.
   *
   * The path runs from x = -12 to x = 112 while the viewBox shows 0..100, so the plane enters
   * and leaves off-stage and the restart is never visible. That is what makes it seamless
   * without a fade.
   *
   * **The loop is two half-arcs, and that is the whole trick.** It used to be one near-complete
   * arc — `a 10,10 0 1,1 0.01,0`, a large-arc flag with the start and end almost on top of each
   * other — and the circle it produced sat in the right place, which is what made the bug so
   * durable: the geometry was right and the *direction of travel* was wrong.
   *
   * With a chord of hundredths of a unit there is nothing left to pin the tangents to, and
   * sampling with `getPointAtLength` showed what actually happened: the plane flew right along
   * the line to (50,30), then the arc's first move was to (48, 29.8) — **left**. A 180-degree
   * cusp, a clockwise loop flown backwards, and another cusp on the way out. With
   * `offset-rotate: auto` the aeroplane snapped round twice per circuit.
   *
   * Two explicit half-arcs through the top of the circle fix the tangents by construction:
   *
   * ```
   * 0:(50,30)  4:(54,29)  10:(58,25)  16:(60,20)  22:(58,14)
   * 31:(50,10) 40:(42,13) 47:(40,20)  53:(42,26)  59:(46,29)  63:(50,30)
   * ```
   *
   * Bottom, right, top, left, bottom — entering rightward and leaving rightward, which is an
   * inside loop: the nose comes up, the aircraft goes over the top inverted and recovers. The
   * old one pitched *down* and flew round backwards.
   */

  interface Props {
    /** Rendered width in pixels. The whole drawing scales with it. */
    width?: number;
    /** Seconds for one complete circuit. Higher is calmer. */
    seconds?: number;
    /** Draw the dashed trail the plane follows. Off for the tiny header mark. */
    trail?: boolean;
    /** Announced to screen readers. Empty means decorative. */
    label?: string;
  }

  const { width = 96, seconds = 5, trail = true, label = '' }: Props = $props();

  // One string, used twice: once as the visible trail and once as the motion path. Two copies
  // that drift apart is a plane flying beside its own line.
  const PATH = 'M -12,30 H 50 A 10,10 0 0,0 50,10 A 10,10 0 0,0 50,30 H 112';
</script>

<svg
  class="plane"
  style="--w:{width}px; --dur:{seconds}s; --path:path('{PATH}');"
  viewBox="0 0 100 44"
  xmlns="http://www.w3.org/2000/svg"
  role={label === '' ? 'presentation' : 'img'}
  aria-label={label === '' ? undefined : label}
  aria-hidden={label === '' ? 'true' : undefined}
>
  {#if trail}
    <path class="trail" d={PATH} />
  {/if}
  <!-- A top-down airliner — swept wings, tailplane, tail cone — drawn around the origin with
       the nose at +x. Both halves matter: offset-rotate:auto aligns 0deg with the direction of
       travel, and offset-path puts the element's *origin* on the line, so a glyph drawn from
       (0,0) outward would trail its own position by half a wingspan.
       It replaced a plain dart, which read as an arrowhead rather than as an aircraft. -->
  <path
    class="craft"
    d="M 3.7,0 L 0.9,-0.55 L -0.9,-3.1 L -1.6,-3.1 L -1.35,-0.55 L -2.5,-0.55 L -3.2,-1.5
       L -3.6,-1.5 L -3.45,-0.5 L -3.85,-0.37 L -3.85,0.37 L -3.45,0.5 L -3.6,1.5 L -3.2,1.5
       L -2.5,0.55 L -1.35,0.55 L -1.6,3.1 L -0.9,3.1 L 0.9,0.55 Z"
  />
</svg>

<style>
  .plane {
    display: inline-block;
    width: var(--w);
    /* Explicit, so a loading row reserves its space before the SVG has laid out and stops
       jumping when it does. */
    height: calc(var(--w) * 0.44);
    /* The path runs past both edges on purpose — this is what hides the restart. */
    overflow: hidden;
    vertical-align: middle;
    flex: none;
    color: inherit;
  }

  /*
    The line the aeroplane flies, drawn so the mark reads as a *track* and not as a plane with a
    decoration behind it. Muted and grey against the accent-coloured aircraft: the eye follows
    the bright thing along the dim one, which is the whole idea of the logo.
  */
  .trail {
    fill: none;
    stroke: var(--trail-colour, currentColor);
    /* Heavier and less transparent than looks right at 120 px, because the mark in the header
       is 64 px wide and at that size a 1.6-wide line at 0.32 opacity is a smudge. The dashes
       have to be legible as dashes or the whole idea — an aircraft flying a track — is lost. */
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-dasharray: 5 5;
    opacity: 0.5;
  }

  .craft {
    fill: var(--craft-colour, currentColor);
    /* Without this the path is read in CSS pixels instead of viewBox units — see the note at
       the top. This one declaration is the difference between a logo and a mess. */
    transform-box: view-box;
    offset-path: var(--path);
    offset-rotate: auto;
    animation: fly var(--dur) linear infinite;
  }

  @keyframes fly {
    from {
      offset-distance: 0%;
    }
    to {
      offset-distance: 100%;
    }
  }

  /* Someone who has asked the system for less motion gets a plane that simply sits on its line.
     Still legible as a mark, still obviously the same drawing, and it does not circle forever in
     the corner of their eye. */
  @media (prefers-reduced-motion: reduce) {
    .craft {
      animation: none;
      offset-distance: 24%;
    }
  }
</style>
